import { useMemo, useState } from "react";
import { C, S } from "../customers/theme";
import { PRODUCT_STATUSES, VAT_RATES } from "../lib/products";

// Accessible labelled field (auto-associates label↔control via a stable id).
let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder, step, suffix, hint, autoFocus }) {
  const id = useMemo(() => `pf-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {options ? (
          <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
            {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input id={id} style={S.input} type={type} step={step} value={value ?? ""} placeholder={placeholder} autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)} />
        )}
        {suffix && <span style={{ color: C.textMuted, fontSize: 13 }}>{suffix}</span>}
      </div>
      {hint && <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  const id = useMemo(() => `pt-${++_seq}`, []);
  return (
    <label htmlFor={id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer", paddingTop: 22 }}>
      <input id={id} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

const NUMERIC = ["wholesale_price", "retail_price", "cost_price", "vat_rate", "drs_rate", "min_order_qty"];

/**
 * Add/Edit product modal. Delegates persistence to the caller's onSave
 * (which calls the products data layer); never touches Supabase directly.
 */
export default function ProductForm({ initial, categories = [], onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const [f, setF] = useState({
    name: seed.name || "",
    sku: seed.sku || "",
    barcode: seed.barcode || "",
    description: seed.description || "",
    category_id: seed.category_id || "",
    unit: seed.unit || "",
    brand: seed.brand || "",
    wholesale_price: seed.wholesale_price ?? "",
    retail_price: seed.retail_price ?? "",
    cost_price: seed.cost_price ?? "",
    vat_rate: seed.vat_rate ?? 23,
    drs_applicable: seed.drs_applicable ?? false,
    drs_rate: seed.drs_rate ?? "",
    min_order_qty: seed.min_order_qty ?? 1,
    in_stock: seed.in_stock ?? true,
    status: seed.status || "active",
    notes: seed.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setError("");
    if (!f.name.trim()) { setError("Product name is required."); return; }
    const payload = { ...f };
    NUMERIC.forEach((k) => { payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]); });
    payload.category_id = f.category_id || null;
    setBusy(true);
    try {
      await onSave(payload);
    } catch (e) {
      setError(e.message || "Could not save product.");
      setBusy(false);
    }
  };

  const categoryOptions = [{ value: "", label: "— No category —" }, ...categories.map((c) => ({ value: c.id, label: c.name }))];

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={S.modalBox}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? `Edit ${seed.name || "Product"}` : "New Product"}
            {seed.product_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.product_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={S.sectionTitle}>Details</div>
        <div style={S.grid2}>
          <Field label="Name *" value={f.name} onChange={set("name")} placeholder="Craft Pale Ale 500ml" autoFocus />
          <Field label="SKU" value={f.sku} onChange={set("sku")} placeholder="BEV-001" />
          <Field label="Barcode" value={f.barcode} onChange={set("barcode")} />
          <Field label="Category" value={f.category_id} onChange={set("category_id")} options={categoryOptions} />
          <Field label="Brand" value={f.brand} onChange={set("brand")} placeholder="Manufacturer / brand" />
          <Field label="Unit" value={f.unit} onChange={set("unit")} placeholder="bottle / box / kg" />
          <Field label="Min Order Qty" type="number" step="1" value={f.min_order_qty} onChange={set("min_order_qty")} />
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Description</label>
          <textarea style={{ ...S.input, height: 60 }} value={f.description} onChange={(e) => set("description")(e.target.value)} />
        </div>

        <div style={S.sectionTitle}>Pricing &amp; VAT</div>
        <div style={S.grid2}>
          <Field label="Wholesale Price" type="number" step="0.01" suffix="€" value={f.wholesale_price} onChange={set("wholesale_price")} placeholder="0.00" />
          <Field label="Retail Price" type="number" step="0.01" suffix="€" value={f.retail_price} onChange={set("retail_price")} placeholder="0.00" />
          <Field label="Cost Price" type="number" step="0.01" suffix="€" value={f.cost_price} onChange={set("cost_price")} placeholder="0.00" />
          <Field label="VAT Rate" value={f.vat_rate} onChange={(v) => set("vat_rate")(v)} options={VAT_RATES} />
          <div style={{ display: "flex", gap: 16 }}>
            <Toggle label="DRS applicable" checked={f.drs_applicable} onChange={set("drs_applicable")} />
            {f.drs_applicable && (
              <div style={{ flex: 1 }}>
                <Field label="DRS Rate" type="number" step="0.01" suffix="€" value={f.drs_rate} onChange={set("drs_rate")} />
              </div>
            )}
          </div>
        </div>

        <div style={S.sectionTitle}>Classification</div>
        <div style={S.grid2}>
          <Field label="Status" value={f.status} onChange={set("status")} options={PRODUCT_STATUSES} />
          <Toggle label="In stock (available to sell)" checked={f.in_stock} onChange={set("in_stock")} />
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Notes</label>
          <textarea style={{ ...S.input, height: 56 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} />
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }} role="alert">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Product"}
          </button>
        </div>
      </div>
    </div>
  );
}
